/**
 * The single tick function: (state, inputs) => state. (§1.4)
 *
 * This is the whole game. Client and server both call ONLY this to advance the
 * world (§15.2). If you are about to write match-affecting logic anywhere else,
 * stop — it belongs behind one of the seams below.
 *
 * ── Pipeline order is load-bearing ─────────────────────────────────────────
 *
 * Each stage depends on the previous one having completed for ALL entities.
 * Reordering these will produce a game that mostly works and is subtly,
 * unfixably wrong. In particular:
 *
 *   - Offside is snapshotted at the exact tick the ball is played (§5.4), so
 *     kick resolution must run BEFORE ball integration moves it off the foot.
 *   - Possession is computed before player control, so the dribble touch model
 *     and the locomotion speed cap agree about whether you have the ball.
 *   - Rules run last, on the settled post-integration state, so a ball that
 *     crossed the line and a foul in the same tick resolve in a defined order.
 */

import type { InputCommand, StepResult, WorldState } from './types.js';
import { MatchPhase, MAX_PLAYERS } from './types.js';
import { FIXED_DT } from './constants.js';
import { stepBall, resolveKicks, updatePossession } from './ball.js';
import { stepPlayerController, stepPlayerCollisions } from './player-controller.js';
import { stepMatchStateMachine, evaluateRules } from './rules/index.js';
import { stepGoalkeeper } from './goalkeeper.js';
import { stepRestartAnim } from './restart-anim.js';
import { beginBallTraceTick, endBallTraceTick } from './ball-trace.js';
import { beginGkTravelTick } from './gk-travel.js';
import type { PhysicsWorld } from './physics.js';

/**
 * Advance the world exactly one FIXED_DT tick.
 *
 * @param state  Mutated in place — zero allocation (§15.7).
 * @param inputs Indexed by PlayerId. `null` = no input this tick (AI-less slot,
 *               disconnected client, or a dropped packet). A null input is NOT
 *               the same as an empty one: null means "repeat last known intent"
 *               is the caller's job, not ours. We treat it as no movement.
 * @param out    Events raised this tick. Caller drains it; we only push.
 * @param physics Rapier world. Same WASM build on both sides (§1.2).
 */
export function step(
  state: WorldState,
  inputs: ReadonlyArray<InputCommand | null>,
  out: StepResult,
  physics: PhysicsWorld,
  /**
   * Optional per-slot human mask (1 ⇒ that slot is driven by a real player this
   * tick). Purely a CONTROL hint supplied by the caller each tick — NOT part of
   * WorldState, so it is never hashed/serialized/cloned and cannot desync. Its
   * only effect: an ACTIVE (in-play) restart will not snap a human's authoritative
   * position — they get a target and move there manually. Omit (all-AI) and the
   * behaviour is exactly as before, which is why determinism is untouched.
   */
  humanMask: Uint8Array | null = null,
): void {
  out.events.length = 0;

  // ── 1. Clock ────────────────────────────────────────────────────────────
  state.tick++;
  if (state.match.phase === MatchPhase.InPlay) {
    state.match.clockTicks++;
  }
  if (state.match.stageTimer > 0) {
    state.match.stageTimer -= FIXED_DT;
    if (state.match.stageTimer < 0) state.match.stageTimer = 0;
  }

  // Ball velocity-change trace (§3.2). Diagnostic side-channel; a no-op unless a
  // sink is attached (setBallTrace). Snapshots the ball velocity now so any net
  // change this tick that no system tagged surfaces as Unexplained at endTick.
  beginBallTraceTick(state);

  // Goalkeeper travel hint (§4.5). Cleared HERE, before stage 2, because one of
  // its two writers is the set-piece walk-in's GK carve-out inside
  // stepMatchStateMachine. Same one-way, same-tick, never-hashed discipline as
  // rules/stage-lock.ts — see gk-travel.ts for why it exists at all.
  beginGkTravelTick(state);

  // ── 2. Match state machine ──────────────────────────────────────────────
  // Decides whether play is live, advances restart setup→ready→live, and gates
  // which inputs are legal this tick (you cannot shoot during a throw-in setup).
  stepMatchStateMachine(state, inputs, out, humanMask);

  const live = state.match.phase === MatchPhase.InPlay;

  // ── 3. Possession ───────────────────────────────────────────────────────
  // Must precede player control: the locomotion cap (MAX_SPEED_DRIBBLE) and the
  // dribble touch model both branch on it.
  updatePossession(state);

  // ── 4. Player control ───────────────────────────────────────────────────
  // Locomotion, momentum, turn-rate-vs-speed, stamina, skill moves, tackles.
  // AI inputs arrive here identical in shape to human ones (§10.1) — this
  // function cannot tell them apart, which is exactly the guarantee we want.
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (state.players.active[i] !== 1) continue;
    if (state.players.sentOff[i] === 1) continue;
    stepPlayerController(state, i, inputs[i] ?? null, out, physics);
  }

  // ── 5. Goalkeeper ───────────────────────────────────────────────────────
  // Runs after outfield control so a keeper reacts to this tick's shot, not
  // last tick's. Human-controlled keepers pass through the same path (§4.5).
  if (state.config.hasGoalkeepers) {
    stepGoalkeeper(state, inputs, out);
  }

  // ── 5b. Restart HAND poses ──────────────────────────────────────────────
  // A throw-in is taken WITH THE HANDS (Law 15) and had no animation at all,
  // because the only module that knows a throw-in is happening — the FSM at
  // stage 2 — is followed by stage 4's stepAnim, which re-derives every anim
  // from velocity and wipes it the same tick. So the pose has to be stamped
  // AFTER player control, which is the identical reason stage 5 exists for the
  // keeper. Pure, derived, allocation-free, and non-authoritative: nothing in
  // /sim branches on `anim`, so this cannot change a match outcome.
  stepRestartAnim(state);

  // ── 6. Kick resolution ──────────────────────────────────────────────────
  // Applies impulse + strike-offset spin to the ball (§3.2) and snapshots the
  // offside line at this exact tick (§5.4). MUST run before stepBall, or the
  // snapshot captures positions one tick late and every tight offside call is
  // wrong by ~14cm at sprint speed.
  if (live) {
    resolveKicks(state, inputs, out);
  }

  // ── 7. Ball integration ─────────────────────────────────────────────────
  // Gravity, drag (with the drag crisis), Magnus, spin decay, ground contact.
  // Inputs flow in for the carrier's dribble touch only: a knock is aimed
  // where the carrier WANTS to go (his stick), not where his velocity still
  // points — velocity lags a turn, and a knock up the old path is the "ball
  // escapes the running line" defect. (Polish §6)
  stepBall(state, inputs, physics);

  // ── 8. Player-player separation ─────────────────────────────────────────
  // Soft shoulder-to-shoulder push. Never hard-locks capsules — that reads as
  // rubber-banding online (§3.3).
  stepPlayerCollisions(state);

  // ── 9. Rules ────────────────────────────────────────────────────────────
  // Pure functions over the settled state (§5). Ball-out, goals, fouls,
  // offside interference, handball, back-pass, 6-second, advantage.
  if (state.config.foulsEnabled || live) {
    evaluateRules(state, out);
  }

  // Close the ball trace: reconcile the tick's net velocity change against every
  // tagged cause; a residual is an Unexplained failing invariant (§3.2).
  endBallTraceTick(state);
}

/**
 * Fixed-timestep driver. (§3.1)
 *
 * Determinism requires the sim NEVER see a variable dt. Everything that wants
 * to advance the world goes through this — the server loop, the client's
 * prediction, the reconciliation re-simulation, and the headless bot harness.
 *
 * The accumulator is the caller's, not ours: reconciliation needs to re-run N
 * exact ticks with no accumulator involved at all (§2.4).
 */
export class FixedStepDriver {
  private accumulator = 0;

  /** Interpolation factor for rendering between the last two sim states. */
  alpha = 0;

  /**
   * @param realDt  Wall-clock delta in seconds.
   * @param onStep  Called once per fixed tick. May be called 0..N times.
   * @returns Number of ticks simulated.
   */
  advance(realDt: number, onStep: () => void): number {
    // Clamp to break the spiral of death: if a GC pause or a background tab
    // stalls us for 2s, we must not then try to simulate 120 ticks in one frame
    // and stall for another 2s. We drop the time instead — the server is
    // authoritative and will correct us anyway.
    this.accumulator += realDt < 0.25 ? realDt : 0.25;

    let ticks = 0;
    while (this.accumulator >= FIXED_DT) {
      onStep();
      this.accumulator -= FIXED_DT;
      ticks++;
    }
    this.alpha = this.accumulator / FIXED_DT;
    return ticks;
  }

  reset(): void {
    this.accumulator = 0;
    this.alpha = 0;
  }
}

/** Reusable StepResult so the loop allocates nothing. (§15.7) */
export function createStepResult(): StepResult {
  return { events: [] };
}
